import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { monitoredObjectTypeConverter } from '../../../../helpers/monitoredObjectTypeConverter';

import { TRootState } from '../../../../store';
import {
    IGetMonitoredObjectsParams,
    restGetRegistersMonitoredObject,
    restGetRegistersMonitoredObjects,
    restPutMonitoredObject,
    TPutRegistersMonitoredObject,
} from '../../../../services/registers/monitoredObjects';
import { IAddress } from '../types';
import { IGroup } from '../../collections/dataTypes';
import { IEntityAttribute, TSystemObject } from '../../../../state/types';

export interface IRegistersMonitoredObject {
    id: number;
    type: TSystemObject;
    name: string;
    objectId: number;
}

export type TMonitoredObjectType = 3 | 8 | 14;
export interface IRegistryEmployee {
    firstName: string;
    surname: string;
    address?: IAddress;
    contact?: {
        email: string;
        phoneNumber: string;
        mobileNumber: string;
    };
}

export interface IRegistryTank {
    logicType: number;
    capacity?: number;
    description?: string;
    fuelType?: string;
    reserve?: number;
    tareWeight?: number;
}
export interface IRegistryVehicle {
    deviceId?: number;
    externalNumber?: string;
    fuelConsumptionType?: string;
    fuelConsumption?: number;
    fuelConsumptionWork?: number;
    fuelType?: string;
    mark?: string;
    model?: string;
    plateNumber?: string;
    productionYear?: number;
    sideNumber?: string;
    tankSize?: number;
    vin?: string;
}

export type TObjectRegistry =
    | IRegistryEmployee
    | IRegistryTank
    | IRegistryVehicle;
export interface IRegistersDetailedMonitoredObject {
    attributes?: IEntityAttribute[];
    externalId: number;
    id: number;
    name: string;
    registry: TObjectRegistry;
    type: TMonitoredObjectType;
    group?: IGroup;
    groups?: IGroup[];
    modifiedAt?: string;
    timeZone?: {
        id: number;
        name: string;
    };
}

export interface IMonitoredObjectItem {
    relationId: string;
    registerId: number;
    registerType: string;
    type: string;
    name: string;
}

interface IState {
    monitoredObjects: IMonitoredObjectItem[];
    objectsLoading: boolean;
}
const initialState: IState = {
    monitoredObjects: [],
    objectsLoading: false,
};

export const fetchRegistersMonitoredObjects = createAsyncThunk(
    'get:app/registers/monitoredObjects',
    async (queryParams?: IGetMonitoredObjectsParams) => {
        const response = await restGetRegistersMonitoredObjects(queryParams);
        return response;
    }
);

export const fetchRegistersMonitoredObject = createAsyncThunk(
    'get:app/registers/monitoredObjects/:id',
    async (objectId: number) => {
        const response = await restGetRegistersMonitoredObject(objectId);
        return response;
    }
);

export const updateMonitoredObject = createAsyncThunk(
    'put:app/registers/monitoredObjects/:id',
    async (
        { monitoredObject }: { monitoredObject: TPutRegistersMonitoredObject },
        { rejectWithValue }
    ) => {
        try {
            const response = await restPutMonitoredObject(monitoredObject);
            return response;
        } catch (e: any) {
            throw rejectWithValue({
                status: e.status,
                errorMsg: e.responseJSON.message,
            });
        }
    }
);

const registersMonitoredObjects = createSlice({
    name: 'registersMonitoredObjects',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchRegistersMonitoredObjects.pending, (state) => {
            state.objectsLoading = true;
        });
        builder.addCase(
            fetchRegistersMonitoredObjects.fulfilled,
            (state, action) => {
                const monitoredObjects = [...action.payload];
                const assignedObjects = [...monitoredObjects].map((object) => {
                    const rightObject = {} as IMonitoredObjectItem;
                    rightObject.name = object.name;
                    rightObject.relationId = String(object.id);
                    rightObject.registerType = monitoredObjectTypeConverter(
                        object.type
                    );
                    rightObject.type = 'MONITORED_OBJECT';
                    rightObject.registerId = object.objectId;
                    return rightObject;
                });
                state.monitoredObjects = assignedObjects;
                state.objectsLoading = false;
            }
        );

        builder.addCase(fetchRegistersMonitoredObjects.rejected, (state) => {
            state.monitoredObjects = [];
            state.objectsLoading = false;
        });
    },
});

export const getMonitoredObjects = (state: TRootState) =>
    state.app.registers.monitoredObjects.monitoredObjects;

export const getMonitoredObjectsLoading = (state: TRootState) =>
    state.app.registers.monitoredObjects.objectsLoading;

export default registersMonitoredObjects.reducer;
